import { z } from 'zod';

import type { Account, AccountSession, OtpChallenge } from '../model/types';

/** Validated at the boundary, mapped straight to the entity type (§7.4). */
const patientDto = z.object({
  id: z.string().min(1),
  /**
   * Null only on a record written before the clinic collected names; the server
   * reports such a profile as incomplete and the form asks for them again.
   */
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  /** Which document keys this patient — the checkbox, decided server-side. */
  isIranian: z.boolean(),
  /*
   * In full, and only to its owner: they have to be able to check the digits
   * they typed.
   *
   * Never both. An Iranian patient always has the first; a foreign one has the
   * second only if they had their passport to hand, so both may be null.
   */
  nationalId: z.string().nullable(),
  passportNumber: z.string().nullable(),
  email: z.string().nullable(),
});

const accountDto = z.object({
  id: z.string().min(1),
  phoneNumber: z.string(),
  locale: z.string(),
  registeredAt: z.string().datetime({ offset: true }),
  /** Null until the holder completes their profile — see `isProfileComplete`. */
  patient: patientDto.nullable(),
  isProfileComplete: z.boolean(),
});

export const accountSchema = accountDto;

export const accountSessionSchema = z.object({
  token: z.string().min(1),
  expiresAt: z.string().datetime({ offset: true }),
  account: accountDto,
  isNewAccount: z.boolean(),
});

export const otpChallengeSchema = z.object({
  expiresAt: z.string().datetime({ offset: true }),
  resendAvailableInSeconds: z.number().int().nonnegative(),
  codeLength: z.number().int().positive(),
  phoneNumber: z.string(),
});

export const accountLogoutSchema = z.object({ revoked: z.boolean() });

export function toAccount(dto: z.infer<typeof accountDto>): Account {
  return {
    id: dto.id,
    phoneNumber: dto.phoneNumber,
    locale: dto.locale,
    registeredAt: new Date(dto.registeredAt),
    patient:
      dto.patient === null
        ? null
        : {
            id: dto.patient.id,
            firstName: dto.patient.firstName,
            lastName: dto.patient.lastName,
            isIranian: dto.patient.isIranian,
            nationalId: dto.patient.nationalId,
            passportNumber: dto.patient.passportNumber,
            email: dto.patient.email,
          },
    isProfileComplete: dto.isProfileComplete,
  };
}

export function toAccountSession(dto: z.infer<typeof accountSessionSchema>): AccountSession {
  return {
    token: dto.token,
    expiresAt: new Date(dto.expiresAt),
    account: toAccount(dto.account),
    isNewAccount: dto.isNewAccount,
  };
}

export function toOtpChallenge(dto: z.infer<typeof otpChallengeSchema>): OtpChallenge {
  return {
    expiresAt: new Date(dto.expiresAt),
    resendAvailableInSeconds: dto.resendAvailableInSeconds,
    codeLength: dto.codeLength,
    phoneNumber: dto.phoneNumber,
  };
}
